import { useState } from 'react'
import { usePlans, useUpdatePlan, useDeletePlan } from '@/hooks/usePlans'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Plan, PlanLimits, PlanFeatures, PlanOrgLimits } from '@/types'
import { formatCurrency, getApiErrorMessage } from '@/lib/utils'
import { Pencil, Users, Star, Zap, Shield, Sparkles, Trash2, Building2 } from 'lucide-react'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { toast } from 'sonner'

const PLATFORM_OPTIONS = ['tiktok', 'instagram', 'google_news', 'twitter', 'facebook']

const FEATURE_LABELS: Record<string, string> = {
  sentiment_analysis: 'Analisis de sentimiento',
  emotion_analysis: 'Analisis de emociones',
  crisis_detection: 'Deteccion de crisis',
  brand_health: 'Salud de marca',
  virality_index: 'Indice de viralidad',
  confidence_index: 'Indice de confianza',
  word_cloud: 'Nube de palabras',
  top_emojis: 'Top emojis',
  alerts_email: 'Alertas email',
  whatsapp_alerts: 'Alertas WhatsApp',
  alerts_slack: 'Alertas Slack',
  csv_export: 'Exportar CSV',
  pdf_reports: 'Exportar PDF',
  white_label: 'Marca blanca',
  competitor_comparison: 'Comparacion competidores',
  influencer_analysis: 'Analisis influencers',
  custom_distribution_mode: 'Modo distribucion personalizado',
  periodic_search: 'Busqueda Periodica',
  scheduled_reports: 'Reportes Programados',
}

interface EditFormState {
  // General
  display_name: string
  description: string
  price_monthly_usd: number
  price_annual_usd: number
  is_featured: boolean
  sort_order: number
  // Credits
  credits_per_month: number
  credits_rollover: boolean
  credits_rollover_max: number
  // Scraping limits
  number_posts: number
  number_comments: number
  max_docs: number
  days_ago: number
  // Limits
  limits: PlanLimits
  // Features
  features: PlanFeatures
  // Org limits
  org_limits: PlanOrgLimits
}

const defaultLimits: PlanLimits = {
  keywords: 0,
  users: 0,
  history_days: 0,
  ai_summaries_per_month: 0,
  pdf_reports_per_month: 0,
  platforms: [],
  max_periodic_searches: 0,
  periodic_min_interval: 0,
  periodic_max_interval: 0,
  periodic_min_credits: 100,
  periodic_max_credits: 750,
  max_scheduled_reports: 0,
}

const defaultFeatures: PlanFeatures = {
  sentiment_analysis: false,
  emotion_analysis: false,
  crisis_detection: false,
  brand_health: false,
  virality_index: false,
  confidence_index: false,
  word_cloud: false,
  top_emojis: false,
  alerts_email: false,
  whatsapp_alerts: false,
  alerts_slack: false,
  csv_export: false,
  pdf_reports: false,
  white_label: false,
  api_access: 'none',
  competitor_comparison: false,
  influencer_analysis: false,
  custom_distribution_mode: false,
  periodic_search: false,
  scheduled_reports: false,
  ai_chat: false,
}

export const PlansListPage = () => {
  const { data: plans, isLoading } = usePlans()
  const updatePlan = useUpdatePlan()
  const [editPlan, setEditPlan] = useState<Plan | null>(null)
  const deletePlan = useDeletePlan()
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null)
  const [editForm, setEditForm] = useState<EditFormState>({
    display_name: '',
    description: '',
    price_monthly_usd: 0,
    price_annual_usd: 0,
    is_featured: false,
    sort_order: 0,
    credits_per_month: 0,
    credits_rollover: false,
    credits_rollover_max: 0,
    number_posts: 0,
    number_comments: 0,
    max_docs: 0,
    days_ago: 0,
    limits: { ...defaultLimits },
    features: { ...defaultFeatures },
    org_limits: { team_enabled: false, max_managed_users: 0, max_invited_members: 0 },
  })

  const openEdit = (plan: Plan) => {
    setEditForm({
      display_name: plan.display_name || '',
      description: plan.description || '',
      price_monthly_usd: plan.price_monthly_usd ?? 0,
      price_annual_usd: plan.price_annual_usd ?? 0,
      is_featured: plan.is_featured === true,
      sort_order: plan.sort_order ?? 0,
      credits_per_month: plan.credits_per_month ?? 0,
      credits_rollover: plan.credits_rollover === true,
      credits_rollover_max: plan.credits_rollover_max ?? 0,
      number_posts: plan.number_posts ?? 0,
      number_comments: plan.number_comments ?? 0,
      max_docs: plan.max_docs ?? 0,
      days_ago: plan.days_ago ?? 0,
      limits: plan.limits ? { ...defaultLimits, ...plan.limits } : { ...defaultLimits },
      features: plan.features ? { ...defaultFeatures, ...plan.features } : { ...defaultFeatures },
      org_limits: plan.org_limits
        ? { ...plan.org_limits }
        : { team_enabled: false, max_managed_users: 0, max_invited_members: 0 },
    })
    setEditPlan(plan)
  }

  const handleSave = async () => {
    if (!editPlan) return
    try {
      const { limits, features, org_limits, ...rest } = editForm
      await updatePlan.mutateAsync({
        id: editPlan.id,
        updates: {
          ...rest,
          credits_per_month: editForm.credits_per_month,
          credits_rollover: editForm.credits_rollover,
          credits_rollover_max: editForm.credits_rollover_max,
          number_posts: editForm.number_posts,
          number_comments: editForm.number_comments,
          max_docs: editForm.max_docs,
          days_ago: editForm.days_ago,
          limits,
          features,
          org_limits,
        },
      })
      setEditPlan(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar plan'))
    }
  }

  const handleToggle = async (plan: Plan) => {
    try {
      await updatePlan.mutateAsync({ id: plan.id, updates: { is_active: !plan.is_active } })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al cambiar estado del plan'))
    }
  }

  const togglePlatform = (platform: string) => {
    setEditForm((f) => {
      const platforms = f.limits.platforms.includes(platform)
        ? f.limits.platforms.filter((p) => p !== platform)
        : [...f.limits.platforms, platform]
      return { ...f, limits: { ...f.limits, platforms } }
    })
  }

  const setFeature = (key: string, value: boolean) => {
    setEditForm((f) => ({
      ...f,
      features: { ...f.features, [key]: value },
    }))
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Planes</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Planes</h1>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {plans?.map((plan) => (
          <Card
            key={plan.id}
            className={!plan.is_active && plan.is_active !== undefined ? 'opacity-60' : ''}
          >
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  {plan.display_name || plan.name}
                  {plan.is_featured && <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />}
                </CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">{plan.name}</p>
              </div>
              <StatusBadge status={plan.is_active === true ? 'active' : 'inactive'} />
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {plan.description || 'Sin descripcion'}
              </p>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Mensual</p>
                  <p className="text-lg font-bold">{formatCurrency(plan.price_monthly_usd)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Anual</p>
                  <p className="text-lg font-bold">{formatCurrency(plan.price_annual_usd)}</p>
                </div>
              </div>

              {(plan.credits_per_month ?? 0) > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <Zap className="h-3.5 w-3.5 text-yellow-500" />
                  <span className="text-muted-foreground">Creditos/mes: </span>
                  <span className="font-medium">
                    {(plan.credits_per_month ?? 0).toLocaleString()}
                  </span>
                </div>
              )}

              {/* Key limits summary */}
              {plan.limits && (
                <div className="flex flex-wrap gap-1">
                  {plan.limits.keywords > 0 && (
                    <Badge variant="default" className="text-xs">
                      {plan.limits.keywords} keywords
                    </Badge>
                  )}
                  {plan.limits.users > 0 && (
                    <Badge variant="default" className="text-xs">
                      {plan.limits.users} usuarios
                    </Badge>
                  )}
                  {plan.limits.history_days > 0 && (
                    <Badge variant="default" className="text-xs">
                      {plan.limits.history_days}d historial
                    </Badge>
                  )}
                  {plan.limits.platforms && plan.limits.platforms.length > 0 && (
                    <Badge variant="default" className="text-xs">
                      {plan.limits.platforms.length} plataformas
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between border-t pt-3">
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  {plan.user_count ?? 0} usuarios
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={plan.is_active === true}
                    onCheckedChange={() => handleToggle(plan)}
                  />
                  <Button variant="ghost" size="sm" onClick={() => openEdit(plan)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingPlan(plan)}
                    disabled={(plan.user_count ?? 0) > 0}
                    title={
                      (plan.user_count ?? 0) > 0
                        ? `No se puede eliminar: ${plan.user_count} usuario(s) asignado(s)`
                        : 'Eliminar plan'
                    }
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Plan Dialog with Tabs */}
      <Dialog open={!!editPlan} onOpenChange={(open) => !open && setEditPlan(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Plan: {editPlan?.display_name}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="credits">Creditos</TabsTrigger>
              <TabsTrigger value="limits">Limites</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
            </TabsList>

            {/* Tab 1 - General */}
            <TabsContent value="general" className="space-y-4 pt-4">
              <div className="grid gap-2">
                <Label>Nombre</Label>
                <Input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Descripcion</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Precio mensual (USD)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={editForm.price_monthly_usd}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, price_monthly_usd: Number(e.target.value) }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Precio anual (USD)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={editForm.price_annual_usd}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, price_annual_usd: Number(e.target.value) }))
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <Label>Destacado</Label>
                  <Switch
                    checked={editForm.is_featured}
                    onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_featured: v }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Orden</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.sort_order}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                    }
                  />
                </div>
              </div>
            </TabsContent>

            {/* Tab 2 - Credits */}
            <TabsContent value="credits" className="space-y-4 pt-4">
              <div className="grid gap-2">
                <Label className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  Creditos por mes
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={editForm.credits_per_month}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, credits_per_month: Number(e.target.value) }))
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Rollover de creditos</Label>
                  <p className="text-xs text-muted-foreground">
                    Permite acumular creditos no usados al siguiente periodo
                  </p>
                </div>
                <Switch
                  checked={editForm.credits_rollover}
                  onCheckedChange={(v) => setEditForm((f) => ({ ...f, credits_rollover: v }))}
                />
              </div>
              {editForm.credits_rollover && (
                <div className="grid gap-2">
                  <Label>Maximo rollover</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.credits_rollover_max}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, credits_rollover_max: Number(e.target.value) }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">0 = sin limite de acumulacion</p>
                </div>
              )}
            </TabsContent>

            {/* Tab 3 - Limits */}
            <TabsContent value="limits" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Posts por busqueda</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.number_posts}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, number_posts: Number(e.target.value) }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Comentarios por busqueda</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.number_comments}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, number_comments: Number(e.target.value) }))
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Max documentos</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.max_docs}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, max_docs: Number(e.target.value) }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Dias de historial</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.days_ago}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, days_ago: Number(e.target.value) }))
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5" />
                    Keywords
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.limits.keywords}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        limits: { ...f.limits, keywords: Number(e.target.value) },
                      }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Usuarios</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.limits.users}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        limits: { ...f.limits, users: Number(e.target.value) },
                      }))
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Dias de historial</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.limits.history_days}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        limits: { ...f.limits, history_days: Number(e.target.value) },
                      }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Resumenes AI/mes</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.limits.ai_summaries_per_month}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        limits: { ...f.limits, ai_summaries_per_month: Number(e.target.value) },
                      }))
                    }
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Reportes PDF/mes</Label>
                <Input
                  type="number"
                  min={0}
                  value={editForm.limits.pdf_reports_per_month}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      limits: { ...f.limits, pdf_reports_per_month: Number(e.target.value) },
                    }))
                  }
                />
              </div>
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2 text-sm font-semibold">
                      <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                      Busqueda Periodica
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Permite a los usuarios crear busquedas automaticas recurrentes
                    </p>
                  </div>
                  <Switch
                    checked={editForm.features.periodic_search === true}
                    onCheckedChange={(v) => setFeature('periodic_search', v)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label
                      className={!editForm.features.periodic_search ? 'text-muted-foreground' : ''}
                    >
                      Max. activas
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      disabled={!editForm.features.periodic_search}
                      value={editForm.limits.max_periodic_searches}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          limits: { ...f.limits, max_periodic_searches: Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label
                      className={!editForm.features.periodic_search ? 'text-muted-foreground' : ''}
                    >
                      Min. intervalo (dias)
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      disabled={!editForm.features.periodic_search}
                      value={editForm.limits.periodic_min_interval}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          limits: { ...f.limits, periodic_min_interval: Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label
                      className={!editForm.features.periodic_search ? 'text-muted-foreground' : ''}
                    >
                      Max. intervalo (dias)
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      disabled={!editForm.features.periodic_search}
                      value={editForm.limits.periodic_max_interval}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          limits: { ...f.limits, periodic_max_interval: Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label
                      className={!editForm.features.periodic_search ? 'text-muted-foreground' : ''}
                    >
                      Min. creditos por ejecucion
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      disabled={!editForm.features.periodic_search}
                      value={editForm.limits.periodic_min_credits}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          limits: { ...f.limits, periodic_min_credits: Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label
                      className={!editForm.features.periodic_search ? 'text-muted-foreground' : ''}
                    >
                      Max. creditos por ejecucion
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      disabled={!editForm.features.periodic_search}
                      value={editForm.limits.periodic_max_credits}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          limits: { ...f.limits, periodic_max_credits: Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2 text-sm font-semibold">
                      <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                      Reportes Programados
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Permite a los usuarios programar envíos automáticos de reportes
                    </p>
                  </div>
                  <Switch
                    checked={editForm.features.scheduled_reports === true}
                    onCheckedChange={(v) => setFeature('scheduled_reports', v)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label
                    className={!editForm.features.scheduled_reports ? 'text-muted-foreground' : ''}
                  >
                    Max. reportes programados
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    disabled={!editForm.features.scheduled_reports}
                    value={editForm.limits.max_scheduled_reports}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        limits: { ...f.limits, max_scheduled_reports: Number(e.target.value) },
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">Usa -1 para ilimitado</p>
                </div>
              </div>
              <div className="space-y-2 rounded-lg border p-3">
                <Label>Plataformas</Label>
                <div className="flex flex-wrap gap-3">
                  {PLATFORM_OPTIONS.map((platform) => (
                    <label key={platform} className="flex items-center gap-2 text-sm capitalize">
                      <Checkbox
                        checked={editForm.limits.platforms.includes(platform)}
                        onCheckedChange={() => togglePlatform(platform)}
                      />
                      {platform.replace('_', ' ')}
                    </label>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Tab 4 - Features */}
            <TabsContent value="features" className="space-y-4 pt-4">
              {' '}
              <div className="space-y-3">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-lg border px-3 py-2"
                  >
                    <Label className="flex items-center gap-2 text-sm">
                      <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                      {label}
                    </Label>
                    <Switch
                      checked={editForm.features[key] === true}
                      onCheckedChange={(v) => setFeature(key, v)}
                    />
                  </div>
                ))}
              </div>
              <div className="grid gap-2 rounded-lg border p-3">
                <Label>Acceso API</Label>
                <Select
                  value={String(editForm.features.api_access ?? 'none')}
                  onValueChange={(v) =>
                    setEditForm((f) => ({ ...f, features: { ...f.features, api_access: v } }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin acceso</SelectItem>
                    <SelectItem value="read_only">Solo lectura</SelectItem>
                    <SelectItem value="full">Completo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label className="flex items-center gap-2 text-sm font-semibold">
                    <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                    Chat IA
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Permite conversar con IA sobre las menciones del dashboard
                  </p>
                </div>
                <Switch
                  checked={editForm.features.ai_chat === true}
                  onCheckedChange={(v) => setFeature('ai_chat', v)}
                />
              </div>
            </TabsContent>

            {/* Tab 5 - Team */}
            <TabsContent value="team" className="space-y-4 pt-4">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label className="flex items-center gap-2 font-semibold">
                    <Building2 className="h-4 w-4" />
                    Habilitar Feature de Equipo
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Permite a los usuarios de este plan gestionar organizaciones y miembros
                  </p>
                </div>
                <Switch
                  checked={editForm.org_limits.team_enabled}
                  onCheckedChange={(v) =>
                    setEditForm((f) => ({ ...f, org_limits: { ...f.org_limits, team_enabled: v } }))
                  }
                />
              </div>
              {editForm.org_limits.team_enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Max. usuarios gestionados</Label>
                    <Input
                      type="number"
                      min={0}
                      value={editForm.org_limits.max_managed_users}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          org_limits: {
                            ...f.org_limits,
                            max_managed_users: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">0 = feature deshabilitado</p>
                  </div>
                  <div className="grid gap-2">
                    <Label>Max. miembros invitados</Label>
                    <Input
                      type="number"
                      min={0}
                      value={editForm.org_limits.max_invited_members}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          org_limits: {
                            ...f.org_limits,
                            max_invited_members: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">0 = feature deshabilitado</p>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPlan(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updatePlan.isPending}>
              {updatePlan.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deletingPlan}
        onOpenChange={(open) => !open && setDeletingPlan(null)}
        title="Eliminar Plan"
        description={`Estas seguro de eliminar el plan "${deletingPlan?.display_name || deletingPlan?.name}"? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={async () => {
          if (deletingPlan) {
            await deletePlan.mutateAsync(deletingPlan.id)
          }
        }}
        isLoading={deletePlan.isPending}
      />
    </div>
  )
}
